// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Shubham Dixit <shubham@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { useState, type ReactNode } from 'react'
import Sidebar from '@/components/Sidebar'
import TopNavbar from '@/components/TopNavbar'
import ActingAsBanner from '@/components/ActingAsBanner'

export default function ProviderLayout({ children }: { children: ReactNode }) {
  const [mobileOpen, setMobileOpen] = useState(false)

  return (
    <div className="bg-secondary">
      <TopNavbar onMobileMenuClick={() => setMobileOpen(true)} />

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Layout row — starts below fixed navbar, fills remaining viewport */}
      <div className="flex mt-14 h-[calc(100vh-3.5rem)] overflow-hidden">
        {/* Sidebar wrapper — fixed overlay on mobile, in-flow on desktop */}
        <div
          className={[
            'fixed top-14 bottom-0 left-0 z-30 flex shrink-0',
            'lg:relative lg:top-auto lg:bottom-auto lg:z-auto',
            'transition-transform duration-300 ease-in-out',
            mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
          ].join(' ')}
        >
          <Sidebar onNavigate={() => setMobileOpen(false)} />
        </div>

        {/* Main content — scrolls independently of sidebar. The act-as banner
            sticks to the top of the scroll area so it's always visible. */}
        <main className="flex-1 min-w-0 overflow-y-auto">
          <ActingAsBanner />
          <div className="px-4 py-4 lg:px-8 lg:py-8">{children}</div>
        </main>
      </div>
    </div>
  )
}
